import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, Heart, CheckCircle, Gift } from 'lucide-react';
import axios from 'axios';
import { isIosApp } from '../utils/platform';

const API = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_EMAIL = 'colotludovic@gmail.com';
const MIN_DONATION = 1;

export const SplashScreen = ({ onComplete }) => {
  const [adConfig, setAdConfig] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [canSkip, setCanSkip] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [currentAdType, setCurrentAdType] = useState(null);
  const [vipAd, setVipAd] = useState(null);
  const [exiting, setExiting] = useState(false);

  const handleComplete = useCallback(() => {
    setExiting(true);
    setTimeout(() => onComplete(), 500);
  }, [onComplete]);

  // Determine which ad to show for alternate mode
  const getAlternateAdType = (config) => {
    if (config.type !== 'alternate') return config.type;
    
    const freq = config.alternate_frequency || '1:1';
    const [customCount, adsenseCount] = freq.split(':').map(Number);
    const totalCycle = customCount + adsenseCount;
    
    // Get and increment visit counter
    const visitCount = parseInt(localStorage.getItem('hypnose_cle_visit_count') || '0', 10);
    localStorage.setItem('hypnose_cle_visit_count', String(visitCount + 1));
    
    const posInCycle = visitCount % totalCycle;
    return posInCycle < customCount ? 'custom' : 'adsense';
  };

  // Check premium status
  useEffect(() => {
    const premiumStatus = localStorage.getItem('hypnose_cle_premium');
    if (premiumStatus === 'true') {
      setIsPremium(true);
      handleComplete();
    }
  }, [handleComplete]);

  // Fire-and-forget tracking of VIP ad views/clicks
  const trackVip = (slot, event) => {
    try {
      const fd = new FormData();
      fd.append('event', event);
      axios.post(`${API}/api/vip-ads/${slot}/track`, fd).catch(() => {});
    } catch (e) { /* ignore */ }
  };

  // Pick the VIP ad to show: weighted rotation among enabled slots
  const pickVipAd = async () => {
    try {
      const res = await axios.get(`${API}/api/vip-ads`);
      const enabled = (res.data || []).filter(a => a.enabled && a.image_url);
      if (enabled.length === 0) return null;
      // Build the rotation cycle: each ad appears `frequency` times
      const cycle = [];
      enabled.forEach(ad => {
        for (let i = 0; i < (ad.frequency || 1); i++) cycle.push(ad);
      });
      const visitCount = parseInt(localStorage.getItem('hypnose_cle_vip_count') || '0', 10);
      localStorage.setItem('hypnose_cle_vip_count', String(visitCount + 1));
      return cycle[visitCount % cycle.length];
    } catch (e) { return null; }
  };

  useEffect(() => {
    if (isPremium) return;
    
    const fetchAdConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/splash-ad`);
        const config = response.data;
        setAdConfig(config);
        setCountdown(config.duration || 5);
        
        // Determine which ad type to actually show
        let resolvedType = getAlternateAdType(config);
        if (resolvedType === 'vip') {
          const picked = await pickVipAd();
          if (picked) {
            setVipAd(picked);
            trackVip(picked.slot, 'view');
          } else resolvedType = 'brand-only';
        }
        // AdSense forbidden on splash by Google policy (no publisher content).
        // Fall back to custom if user has one; otherwise we still display the
        // brand hero image splash for a short moment.
        if (resolvedType === 'adsense') {
          resolvedType = config.image_url ? 'custom' : 'brand-only';
        }
        if (config.type === 'none') resolvedType = 'brand-only';
        setCurrentAdType(resolvedType);
        // brand-only splash: short 2.5s display so the user sees the hero image
        if (resolvedType === 'brand-only') {
          setCountdown(2);
        }
      } catch (error) {
        console.error('Error fetching ad config:', error);
        // even on error, briefly show the brand splash
        setAdConfig({ type: 'brand', duration: 2 });
        setCurrentAdType('brand-only');
        setCountdown(2);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdConfig();
  }, [handleComplete, isPremium]);

  // Countdown timer
  useEffect(() => {
    if (loading || !adConfig || isPremium) return;

    const duration = currentAdType === 'brand-only' ? 2 : (adConfig.duration || 5);

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setCanSkip(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto-skip after countdown + 0.8s (brand) or +2s (with ad)
    const extra = currentAdType === 'brand-only' ? 0.8 : 2;
    const autoSkip = setTimeout(() => {
      handleComplete();
    }, (duration + extra) * 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(autoSkip);
    };
  }, [loading, adConfig, currentAdType, handleComplete, isPremium]);

  // Load AdSense script if needed
  useEffect(() => {
    if (currentAdType === 'adsense') {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // AdSense may not be loaded yet
      }
    }
  }, [currentAdType]);

  const handleSkip = () => {
    if (canSkip) {
      handleComplete();
    }
  };

  const handleAdClick = () => {
    if (adConfig?.link_url) {
      window.open(adConfig.link_url, '_blank');
    }
  };

  const handleDonate = () => {
    // Ouvrir PayPal avec le montant minimum
    const paypalUrl = `https://www.paypal.com/donate/?business=${PAYPAL_EMAIL}&amount=${MIN_DONATION}&currency_code=EUR&item_name=Don%20Hypnose clé%20-%20Supprimer%20les%20publicit%C3%A9s`;
    window.open(paypalUrl, '_blank');
  };

  const confirmDonation = () => {
    // Marquer l'utilisateur comme premium
    localStorage.setItem('hypnose_cle_premium', 'true');
    localStorage.setItem('hypnose_cle_premium_date', new Date().toISOString());
    setIsPremium(true);
    setShowDonationModal(false);
    handleComplete();
  };

  if (isPremium) {
    return null;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!adConfig) {
    return null;
  }

  return (
    <div className={`fixed inset-0 z-[9999] ${exiting ? 'splash-exit' : ''}`} style={{ backgroundColor: '#02080c' }}>
      {/* Fullscreen brand hero image */}
      <img
        src="/cle-splash.png"
        alt="Hypnose clé"
        className="absolute inset-0 w-full h-full object-cover splash-hero-anim"
        data-testid="splash-hero-image"
      />
      {/* Soft dark gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70 pointer-events-none" />

      {/* Top Bar - Skip & Remove Ads */}
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
        {/* Remove Ads Button (masqué sur la version App Store) */}
        {!isIosApp() ? (
        <button
          onClick={() => setShowDonationModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 hover:border-pink-400/50 text-pink-400 hover:text-pink-300 transition-all"
          data-testid="remove-ads-btn"
        >
          <Heart className="w-4 h-4" />
          <span className="text-sm">Supprimer les pubs</span>
        </button>
        ) : <span />}
        
        {/* Skip Button */}
        <button
          onClick={handleSkip}
          disabled={!canSkip}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            canSkip 
              ? 'bg-white/20 hover:bg-white/30 text-white cursor-pointer' 
              : 'bg-white/10 text-white/50 cursor-not-allowed'
          }`}
          data-testid="skip-ad-btn"
        >
          {canSkip ? (
            <>
              <span>Continuer</span>
              <X className="w-4 h-4" />
            </>
          ) : (
            <span>Passer dans {countdown}s</span>
          )}
        </button>
      </div>

      {/* Ad Content (overlayed at bottom of fullscreen hero) */}
      <div className="absolute bottom-10 left-0 right-0 px-4 z-10 flex flex-col items-center">
        {/* Brand wordmark */}
        <h1
          className="text-center text-4xl sm:text-5xl tracking-wide drop-shadow-[0_4px_24px_rgba(0,0,0,0.85)]"
          style={{ fontFamily: "'Playfair Display', serif", color: '#fbbf24' }}
        >
          Hypnose clé
        </h1>
        <p className="text-amber-100/85 text-sm sm:text-base mt-2 italic drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
          Votre clé vers la sérénité
        </p>

        {/* VIP Ad (weighted rotation) — full image clickable to the link */}
        {currentAdType === 'vip' && vipAd && (
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mt-5 max-w-sm w-full" data-testid="vip-splash-ad">
            {vipAd.link_url ? (
              <a href={vipAd.link_url} target="_blank" rel="noopener noreferrer" data-testid="vip-ad-image-link" onClick={() => trackVip(vipAd.slot, 'click')}>
                <img
                  src={vipAd.image_url.startsWith('http') ? vipAd.image_url : `${API}${vipAd.image_url}`}
                  alt={`Publicité VIP ${vipAd.slot}`}
                  className="w-full h-auto max-h-[25vh] object-contain"
                />
              </a>
            ) : (
              <img
                src={vipAd.image_url.startsWith('http') ? vipAd.image_url : `${API}${vipAd.image_url}`}
                alt={`Publicité VIP ${vipAd.slot}`}
                className="w-full h-auto max-h-[25vh] object-contain"
              />
            )}
            {vipAd.link_url && (
              <a
                href={vipAd.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-3 right-3 flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium text-xs transition-all hover:scale-105 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #b45309, #d97706, #f59e0b)' }}
                data-testid="vip-ad-learn-more"
                onClick={(e) => { e.stopPropagation(); trackVip(vipAd.slot, 'click'); }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>En savoir plus</span>
              </a>
            )}
          </div>
        )}

        {/* Custom Image Ad (small banner under wordmark if user has one) */}
        {currentAdType === 'custom' && adConfig.image_url && (
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mt-5 max-w-sm w-full">
            <img 
              src={adConfig.image_url.startsWith('http') ? adConfig.image_url : `${API}${adConfig.image_url}`}
              alt="Publicité"
              className="w-full h-auto max-h-[25vh] object-contain"
            />
            {adConfig.link_url && (
              <a
                href={adConfig.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-3 right-3 flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium text-xs transition-all hover:scale-105 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #b45309, #d97706, #f59e0b)' }}
                data-testid="ad-learn-more"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>En savoir plus</span>
              </a>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes splashHero {
          0% { opacity: 0; transform: scale(1.08); filter: blur(10px); }
          60% { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        .splash-hero-anim { animation: splashHero 1.4s ease-out forwards; }
        .splash-exit { opacity: 0; transition: opacity 0.5s ease; }
      `}</style>

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="p-6 text-center border-b border-white/10 bg-gradient-to-r from-pink-500/10 to-purple-500/10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-white text-xl font-heading font-semibold mb-2">
                Supprimer les publicités
              </h2>
              <p className="text-white/60 text-sm">
                Soutenez Hypnose clé et profitez d'une expérience sans pub
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="text-center">
                <p className="text-white/80 mb-4">
                  Faites un don de <span className="text-amber-400 font-bold">{MIN_DONATION}€ minimum</span> pour supprimer définitivement les publicités.
                </p>
                
                <div className="bg-white/5 rounded-xl p-4 mb-4">
                  <p className="text-white/60 text-sm mb-3">Ce que vous obtenez :</p>
                  <ul className="text-left space-y-2">
                    <li className="flex items-center gap-2 text-white/80 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      Plus aucune publicité à l'ouverture
                    </li>
                    <li className="flex items-center gap-2 text-white/80 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      Accès direct à l'application
                    </li>
                    <li className="flex items-center gap-2 text-white/80 text-sm">
                      <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      Soutenez le développement de Hypnose clé
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 1 - Donate */}
              <div className="space-y-3">
                <p className="text-white/50 text-xs uppercase tracking-wider">Étape 1 : Faire un don</p>
                <button
                  onClick={handleDonate}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-slate-900 font-semibold flex items-center justify-center gap-2 transition-all"
                  data-testid="paypal-donate-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .632-.54h6.012c2.656 0 4.63.57 5.865 1.695 1.09.992 1.62 2.348 1.577 4.03-.14 5.612-3.88 7.337-7.465 7.337H9.168a.79.79 0 0 0-.78.666l-.913 5.893a.641.641 0 0 1-.633.536h.234z"/>
                    <path d="M19.946 8.211c-.14 5.612-3.88 7.337-7.465 7.337h-2.397a.79.79 0 0 0-.78.666l-1.174 7.593a.484.484 0 0 0 .478.558h3.136a.641.641 0 0 0 .633-.54l.026-.134.712-4.615.046-.244a.641.641 0 0 1 .632-.54h.398c2.58 0 4.6-.1 6.36-1.584 1.548-1.304 2.392-3.348 2.623-6.264.085-1.075.033-1.97-.162-2.71-.19-.718-.514-1.318-.97-1.818a6.99 6.99 0 0 1-.096 2.295z"/>
                  </svg>
                  Faire un don via PayPal
                </button>
              </div>

              {/* Step 2 - Confirm */}
              <div className="space-y-3 pt-2">
                <p className="text-white/50 text-xs uppercase tracking-wider">Étape 2 : Confirmer votre don</p>
                <button
                  onClick={confirmDonation}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-semibold flex items-center justify-center gap-2 transition-all"
                  data-testid="confirm-donation-btn"
                >
                  <CheckCircle className="w-5 h-5" />
                  J'ai fait mon don
                </button>
                <p className="text-white/40 text-xs text-center">
                  En cliquant, vous confirmez avoir fait un don d'au moins {MIN_DONATION}€
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10">
              <button
                onClick={() => setShowDonationModal(false)}
                className="w-full py-2 text-white/50 hover:text-white/80 text-sm transition-colors"
              >
                Non merci, continuer avec les publicités
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SplashScreen;
